import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { faqCrud } from "../api/ApiRoutesFile";

export const faqApiSlice = createApi({
  reducerPath: "faqApi",
  baseQuery: fetchBaseQuery({
    baseUrl: "https://castle-auction-production.up.railway.app/api/",
    prepareHeaders: (headers) => {
      headers.set("Content-Type", "application/json");
      const token = localStorage.getItem("auction_portal");
      if (token) {
        headers.set("x-auth-token", token);
      }
      return headers;
    },
  }),
  tagTypes: ["Faqs"],
  endpoints: (builder) => ({
    faqs: builder.query({
      query: ({ endpoint, id }) => {
        const url = id ? `${endpoint}/${id}` : endpoint;
        return {
          url,
          method: "GET",
        };
      },
      providesTags: (result) =>
        result
          ? [
              ...result.faqs.map(({ _id }) => ({
                type: "Faqs",
                id: _id,
              })),
              { type: "Faqs", id: "LIST" },
            ]
          : [{ type: "Faqs", id: "LIST" }],
    }),

    createFaq: builder.mutation({
      query: ({ endpoint, formData }) => ({
        url: endpoint,
        method: "POST",
        body: formData,
      }),
      // Wait for the API response before updating the cache
      async onQueryStarted(
        { endpoint, formData, lastId },
        { dispatch, queryFulfilled }
      ) {
        try {
          const { data } = await queryFulfilled; // wait for the real API response
          if (data && data.faqs && data.faqs._id) {
            // Update the paginated query cache (if using a page-based cache)
            dispatch(
              faqApiSlice.util.updateQueryData(
                "faqs",
                { endpoint: faqCrud, id: lastId },
                (draft) => {
                  // Insert the new lot at the beginning (top)
                  draft.faqs.unshift(data.faqs);
                }
              )
            );
          }
        } catch (error) {
          console.error("Create failed:", error);
        }
      },
    }),

    updateFaq: builder.mutation({
      query: ({ endpoint, id, formData }) => ({
        url: `${endpoint}/${id}`,
        method: "PUT",
        body: formData,
      }),
      // Wait for API response before updating the cache
      async onQueryStarted(
        { endpoint, id, formData, lastId },
        { dispatch, queryFulfilled }
      ) {
        try {
          const { data } = await queryFulfilled; // wait for the API call to complete
          if (data && data.faqs) {
            // Update paginated cache if applicable
            dispatch(
              faqApiSlice.util.updateQueryData(
                "faqs",
                { endpoint: faqCrud, id: lastId },
                (draft) => {
                  const index = draft.faqs.findIndex((a) => a._id === id);
                  if (index !== -1) {
                    draft.faqs[index] = data.faqs;
                  }
                }
              )
            );
          }
        } catch (error) {
          console.error("Update failed:", error);
          // Handle rollback if needed
        }
      },
    }),

    deleteFaq: builder.mutation({
      query: ({ endpoint, id }) => ({
        url: `${endpoint}/${id}`,
        method: "DELETE",
      }),

      async onQueryStarted(
        { endpoint, id, lastId },
        { dispatch, queryFulfilled }
      ) {
        try {
          const { data } = await queryFulfilled;

          if (data && data?.faqs) {
            dispatch(
              faqApiSlice.util.updateQueryData(
                "faqs",
                { endpoint: faqCrud, id: lastId },
                (draft) => {
                  if (draft && draft.faqs) {
                    draft.faqs = draft.faqs.filter((faq) => faq._id !== id);
                  }
                }
              )
            );
          }
        } catch (error) {
          console.error("Delete FAQ failed:", error);
        }
      },
    }),
  }),
});

export const {
  useFaqsQuery,
  useCreateFaqMutation,
  useUpdateFaqMutation,
  useDeleteFaqMutation,
} = faqApiSlice;
